import { useMemo } from 'react';
import { classNames } from 'primereact/utils';
import { useGetToolkitQuery, type ToolkitTool } from '@/api';
import { CHAT_INPUT_TEXTAREA_ID } from '@/ui/features/chat/components/NextSteps';
import { useFrostedGlassClass } from '@/ui/theme/useFrostedGlassClass';

interface ToolkitCarouselProps {
  /** Fired when the user picks a tile — populates the chat input, which then takes focus. */
  onSelectPrompt: (prompt: string) => void;
}

/** The user's own tools have no group; the toolkit modal lists them under this heading. */
const USER_TOOLS_GROUP_TITLE = 'My prompts';

interface CarouselTool {
  tool: ToolkitTool;
  /** Several practice areas have a tool with the same name, so the group tells the cards apart. */
  groupTitle: string;
}

/**
 * Auto-scrolling carousel of all toolkit prompts, randomly ordered on mount. Two copies of the list
 * and a `-50%` translate make a seamless loop. Edges fade with a mask.
 */
export const ToolkitCarousel = ({ onSelectPrompt }: ToolkitCarouselProps) => {
  const { data } = useGetToolkitQuery({});
  const frostedGlassClass = useFrostedGlassClass();

  const allTools = useMemo<CarouselTool[]>(() => {
    if (!data) return [];
    const grouped = data.toolkitGroupCollections.flatMap((collection) =>
      collection.toolGroups.flatMap((group) => group.tools.map((tool) => ({ tool, groupTitle: group.groupTitle }))),
    );
    return [...grouped, ...data.userTools.map((tool) => ({ tool, groupTitle: USER_TOOLS_GROUP_TITLE }))];
  }, [data]);

  // Shuffled once per mount.
  const shuffled = useMemo(() => shuffleArray(allTools), [allTools]);

  if (shuffled.length === 0) return null;

  const doubledTools = [...shuffled, ...shuffled];

  const handleSelect = (prompt: string) => {
    onSelectPrompt(prompt);
    // Caret first: focusing scrolls to the old caret, which the new prompt left at the end.
    requestAnimationFrame(() => {
      const textarea = document.getElementById(CHAT_INPUT_TEXTAREA_ID) as HTMLTextAreaElement | null;
      if (!textarea) return;
      textarea.setSelectionRange(0, 0);
      textarea.focus();
    });
  };

  return (
    <div className="toolkit-carousel-mask relative w-full overflow-hidden pt-2 pb-2">
      <style>{CAROUSEL_STYLES}</style>
      <div className="toolkit-carousel-track flex w-max gap-3">
        {doubledTools.map(({ tool, groupTitle }, index) => (
          <button
            key={`${tool.toolId}-${index}`}
            type="button"
            // The second copy only exists for the loop; keep it out of the tab order and screen readers.
            inert={index >= shuffled.length}
            aria-hidden={index >= shuffled.length || undefined}
            onClick={() => handleSelect(tool.prompt)}
            className={classNames(
              'border-theme-border-grey hover:!bg-theme-white/85 flex min-h-[112px] w-64 shrink-0 cursor-pointer flex-col items-start gap-1.5 rounded-xl border px-4 py-4 text-left shadow-sm transition-[background-color,box-shadow] hover:shadow-md',
              frostedGlassClass,
            )}
          >
            <span className="text-theme-text-grey w-full truncate text-xs">{groupTitle}</span>
            <span className="text-theme-black w-full truncate text-sm font-semibold">{tool.toolName}</span>
            {tool.description && (
              <span className="text-theme-text-grey line-clamp-3 text-[0.8125rem]">{tool.description}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

// Fisher-Yates. `Math.random` is fine for a UI shuffle — no crypto needed.
const shuffleArray = <T,>(input: readonly T[]): T[] => {
  const arr = [...input];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j]!, arr[i]!];
  }
  return arr;
};

const CAROUSEL_STYLES = `
.toolkit-carousel-mask {
  -webkit-mask-image: linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%);
  mask-image: linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%);
}

.toolkit-carousel-track {
  animation: toolkit-carousel-scroll 2400s linear infinite;
}

.toolkit-carousel-mask:hover .toolkit-carousel-track,
.toolkit-carousel-mask:focus-within .toolkit-carousel-track {
  animation-play-state: paused;
}

@keyframes toolkit-carousel-scroll {
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
}

@media (prefers-reduced-motion: reduce) {
  .toolkit-carousel-track { animation: none; }
}
`;
